import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Term } from "@/shared/components/tooltip/Term.tsx"
import {
	AdminClient,
	type AuditEntry,
	type McpServerSummary,
	type ServiceSummary,
	type ToolCatalog
} from "../api/admin-client.ts"
import type { AuthContext } from "../components/AdminAuthGate.tsx"
import { AuditTimeline } from "../components/AuditTimeline.tsx"
import { CopyRow } from "../components/CopyRow.tsx"
import { OAuthConnectionsSection } from "../components/OAuthConnectionsSection.tsx"
import { OntologyEditor } from "../components/ontology/OntologyEditor.tsx"
import { OntologyPacksPanel } from "../components/ontology/OntologyPacksPanel.tsx"
import { ServerCreateModal } from "../components/ServerCreateModal.tsx"
import { Skeleton, SkeletonText } from "../components/Skeleton.tsx"
import { SuspendModal } from "../components/SuspendModal.tsx"
import { AsOfProvider } from "../hooks/useAsOf.tsx"
import { useAsyncData } from "../hooks/useAsyncData.ts"
import { AssociationsTab } from "./service-detail/components/AssociationsTab.tsx"
import { DocumentsBulkUploadTab } from "./service-detail/components/DocumentsBulkUploadTab.tsx"
import { GraphTabWithPlayback } from "./service-detail/components/GraphTabWithPlayback.tsx"

type TabKey = "overview" | "ontology" | "servers" | "graph" | "documents" | "associations" | "audit"

const TABS: ReadonlyArray<{ readonly key: TabKey; readonly label: React.ReactNode }> = [
	{ key: "overview", label: "개요" },
	{ key: "ontology", label: <Term name="slice">온톨로지</Term> },
	{
		key: "servers",
		label: (
			<>
				<Term name="mcp">MCP</Term> 서버
			</>
		)
	},
	{ key: "graph", label: <Term name="kg">그래프</Term> },
	{ key: "documents", label: "Documents" },
	{ key: "associations", label: "Associations" },
	{ key: "audit", label: "감사 로그" }
]

const TAB_KEYS = TABS.map((t) => t.key) as ReadonlyArray<TabKey>
/** `?tab=` 파라미터 파싱 — 허용된 키가 아니면 기본 overview. 새로고침/공유 URL 에서 탭 상태 복원. */
function parseTabParam(raw: string | null): TabKey {
	return raw && (TAB_KEYS as ReadonlyArray<string>).includes(raw) ? (raw as TabKey) : "overview"
}

/** 서비스 상태 표시 텍스트. */
function statusLabel(status: string): string {
	return status === "active" ? "활성" : "정지"
}

export function ServiceDetailPage(props: { readonly auth: AuthContext }) {
	const { slug } = useParams<{ slug: string }>()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const [service, setService] = useState<ServiceSummary | undefined>()
	const [error, setError] = useState<string | undefined>()
	const tab = parseTabParam(searchParams.get("tab"))
	const setTab = useCallback(
		(next: TabKey) => {
			setSearchParams(
				(prev) => {
					const p = new URLSearchParams(prev)
					if (next === "overview") p.delete("tab")
					else p.set("tab", next)
					return p
				},
				{ replace: false }
			)
		},
		[setSearchParams]
	)
	const [deleting, setDeleting] = useState(false)
	const [serverCount, setServerCount] = useState<number | undefined>()
	const [auditCount, setAuditCount] = useState<number | undefined>()
	// Audit preload — OverviewTab 최근 활동 타임라인에서 재사용 (AuditTab 중복 fetch 는 별건)
	const [auditEntries, setAuditEntries] = useState<ReadonlyArray<AuditEntry>>([])

	const client = useMemo(
		() => new AdminClient(props.auth.baseUrl, props.auth.invalidateSession),
		[props.auth.baseUrl, props.auth.invalidateSession]
	)

	const refreshService = useCallback(async () => {
		if (!slug) return
		try {
			setService(await client.getService(slug))
		} catch (e) {
			setError((e as Error).message)
		}
	}, [slug, client])

	useEffect(() => {
		if (!slug) return
		let cancelled = false
		client
			.getService(slug)
			.then((s) => {
				if (!cancelled) setService(s)
			})
			.catch((e: Error) => {
				if (!cancelled) setError(e.message)
			})
		return () => {
			cancelled = true
		}
	}, [slug, client])

	// TabBar 의 카운트 배지 용 경량 preload — 실패해도 탭 자체는 동작한다.
	useEffect(() => {
		if (!slug) return
		let cancelled = false
		void (async () => {
			try {
				const [servers, audit] = await Promise.all([client.listMcpServers(slug), client.listAudit(slug, 200)])
				if (!cancelled) {
					setServerCount(servers.length)
					setAuditCount(audit.length)
					setAuditEntries(audit)
				}
			} catch {
				// best-effort
			}
		})()
		return () => {
			cancelled = true
		}
	}, [slug, client])

	async function onDeleteService() {
		if (!slug || !service) return
		const confirmed = window.confirm(
			`"${service.displayName}" (${slug}) 을(를) 정말 삭제할까요?\n\n모든 MCP 서버와 API 키가 복구 불가하게 제거됩니다.`
		)
		if (!confirmed) return
		setError(undefined)
		setDeleting(true)
		try {
			await client.deleteService(slug)
			navigate("/services")
		} catch (e) {
			setError((e as Error).message)
			setDeleting(false)
		}
	}

	if (!slug)
		return (
			<div className="content is-wide">
				<div className="alert alert-danger">slug 없음</div>
			</div>
		)
	if (error)
		return (
			<div className="content is-wide">
				<div className="alert alert-danger">{error}</div>
			</div>
		)
	if (!service)
		return (
			<div className="content is-wide">
				<div className="breadcrumb">
					<Skeleton width={80} height={12} inline />
				</div>
				<div className="panel" aria-hidden>
					<div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
						<Skeleton width={40} height={40} radius={10} />
						<div style={{ flex: 1 }}>
							<Skeleton width="40%" height={18} />
							<div style={{ height: 6 }} />
							<Skeleton width="65%" height={12} />
						</div>
					</div>
					<SkeletonText lines={3} lineHeight={12} />
				</div>
				<div className="stats">
					{Array.from({ length: 4 }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
						<div key={i} className="stat">
							<Skeleton width={60} height={26} radius={6} />
							<div style={{ height: 6 }} />
							<Skeleton width="70%" height={10} />
						</div>
					))}
				</div>
			</div>
		)

	return (
		<div className="content is-wide">
			<div className="breadcrumb">
				<Link to="/services">서비스</Link> / <span>{service.serviceSlug}</span>
			</div>

			<div className="page-head">
				<div>
					<h2>{service.displayName}</h2>
					<div className="sub">
						<code className="inline">{service.serviceSlug}</code> ·{" "}
						<span className={service.status === "active" ? "success" : "muted"}>{service.status}</span>
					</div>
				</div>
				<button type="button" className="btn btn-danger btn-sm" onClick={onDeleteService} disabled={deleting}>
					{deleting ? "삭제 중…" : "서비스 삭제"}
				</button>
			</div>

			<TabBar
				active={tab}
				onChange={setTab}
				counts={{
					ontology:
						service.ontologySlice.kinds.length +
						service.ontologySlice.domains.length +
						service.ontologySlice.relations.length,
					servers: serverCount,
					audit: auditCount
				}}
			/>

			{tab === "overview" && (
				<OverviewTab
					auth={props.auth}
					client={client}
					service={service}
					refresh={refreshService}
					serverCount={serverCount}
					auditCount={auditCount}
					auditEntries={auditEntries}
					onEditPacks={() => setTab("ontology")}
					onGoServers={() => setTab("servers")}
					onGoAudit={() => setTab("audit")}
					onGoGraph={() => setTab("graph")}
				/>
			)}
			{tab === "ontology" && <OntologyTab client={client} service={service} refresh={refreshService} />}
			{tab === "servers" && (
				<ServersTab auth={props.auth} client={client} service={service} onCount={(n) => setServerCount(n)} />
			)}
			{tab === "graph" && (
				<AsOfProvider>
					<GraphTabWithPlayback slug={service.serviceSlug} client={client} />
				</AsOfProvider>
			)}
			{tab === "documents" && <DocumentsBulkUploadTab client={client} serviceSlug={service.serviceSlug} />}
			{tab === "associations" && <AssociationsTab client={client} slug={service.serviceSlug} />}
			{tab === "audit" && (
				<AuditTab client={client} slug={service.serviceSlug} onCount={(n) => setAuditCount(n)} />
			)}
		</div>
	)
}

function TabBar(props: {
	readonly active: TabKey
	readonly onChange: (next: TabKey) => void
	readonly counts?: Readonly<Partial<Record<TabKey, number | undefined>>>
}) {
	return (
		<div className="tab-bar">
			{TABS.map((t) => {
				const isActive = props.active === t.key
				const count = props.counts?.[t.key]
				return (
					<button
						key={t.key}
						type="button"
						onClick={() => props.onChange(t.key)}
						className={`tab-btn${isActive ? " is-active" : ""}`}
					>
						{t.label}
						{count !== undefined && <span className="tab-count">{count}</span>}
					</button>
				)
			})}
		</div>
	)
}

function OverviewTab(props: {
	readonly auth: AuthContext
	readonly client: AdminClient
	readonly service: ServiceSummary
	readonly refresh: () => Promise<void>
	readonly serverCount: number | undefined
	readonly auditCount: number | undefined
	readonly auditEntries: ReadonlyArray<AuditEntry>
	/** "Edit packs" 클릭 시 호출 — ServiceDetailPage 가 Ontology 탭으로 전환한다. */
	readonly onEditPacks: () => void
	readonly onGoServers: () => void
	readonly onGoAudit: () => void
	readonly onGoGraph: () => void
}) {
	const [suspendOpen, setSuspendOpen] = useState(false)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()

	async function onActivate() {
		setBusy(true)
		setError(undefined)
		try {
			await props.client.activateService(props.service.serviceSlug)
			await props.refresh()
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<>
			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 18 }}>
					{error}
				</div>
			)}

			<ServiceHero
				baseUrl={props.auth.baseUrl}
				service={props.service}
				busy={busy}
				onActivate={onActivate}
				onSuspend={() => setSuspendOpen(true)}
			/>

			{/*
			 * OverviewStat 4개 + QuickActionCard 3개 블록 제거 — 상단 TabBar 과 기능 중복.
			 * 사용자 피드백(2026-04) 에 따라 Overview 는 Hero → 서비스 정보 → 최근 활동 → 팩 패널로만 구성.
			 */}

			<div className="panel service-overview-info">
				<div className="panel-head">
					<h3>서비스 정보</h3>
				</div>
				<dl className="kv">
					<dt>오너</dt>
					<dd>
						<a href={`mailto:${props.service.ownerEmail}`} className="service-overview-owner">
							{props.service.ownerEmail}
						</a>
					</dd>
					<dt>설명</dt>
					<dd className={props.service.description ? "" : "muted"}>
						{props.service.description || "설명 없음"}
					</dd>
					<dt>생성일</dt>
					<dd className="muted">{new Date(props.service.createdAt).toLocaleString("ko-KR")}</dd>
					{props.service.suspendedReason && (
						<>
							<dt>정지 사유</dt>
							<dd className="warning">{props.service.suspendedReason}</dd>
						</>
					)}
				</dl>
			</div>

			<RecentActivityPanel
				entries={props.auditEntries}
				totalCount={props.auditCount}
				onGoAudit={props.onGoAudit}
			/>

			<OntologyPacksPanel
				slice={props.service.ontologySlice}
				client={props.client}
				onEditPacks={props.onEditPacks}
			/>

			<OAuthConnectionsSection client={props.client} slug={props.service.serviceSlug} />

			{suspendOpen && (
				<SuspendModal
					slug={props.service.serviceSlug}
					onClose={() => setSuspendOpen(false)}
					onConfirm={async (reason) => {
						await props.client.suspendService(props.service.serviceSlug, reason)
						await props.refresh()
					}}
				/>
			)}
		</>
	)
}

/**
 * Service Hero — Overview 최상단 카드. 서비스의 핵심 자산인 MCP 엔드포인트 URL 과
 * 주요 상태 전환 액션(일시정지/재활성화) 을 노출한다. 상단 page-head 는 메타(slug/status)
 * 를 간결하게 표시하고, Hero 는 "복사 가능한 자산 + 액션" 에 집중.
 */
function ServiceHero(props: {
	readonly baseUrl: string
	readonly service: ServiceSummary
	readonly busy: boolean
	readonly onActivate: () => void
	readonly onSuspend: () => void
}) {
	const { baseUrl, service, busy, onActivate, onSuspend } = props
	const endpoint = `${baseUrl}/mcp/${service.serviceSlug}`
	const isActive = service.status === "active"
	return (
		<div className="service-overview-hero">
			<div className="service-overview-hero-main">
				<div className="service-overview-hero-title">
					<span
						className={`badge service-overview-hero-status ${isActive ? "badge-active" : "badge-disabled"}`}
					>
						{statusLabel(service.status)}
					</span>
					<h3>{service.displayName}</h3>
				</div>
				<div className="service-overview-hero-endpoint">
					<span className="service-overview-hero-endpoint-label">MCP 엔드포인트</span>
					<CopyRow value={endpoint} />
				</div>
			</div>
			<div className="service-overview-hero-actions">
				{isActive ? (
					<button type="button" className="btn btn-ghost btn-sm" onClick={onSuspend} disabled={busy}>
						일시정지
					</button>
				) : (
					<button type="button" className="btn btn-primary btn-sm" onClick={onActivate} disabled={busy}>
						{busy ? "처리 중…" : "재활성화"}
					</button>
				)}
			</div>
		</div>
	)
}

/**
 * 최근 활동 패널 — OverviewTab 전용. 상위에서 preload 한 audit entries 중 최근 N개만
 * `AuditTimeline` 으로 렌더하고, "전체 보기" 로 Audit 탭 점프를 제공한다.
 * entries 가 아직 비었으면 로딩/빈 상태를 구분해 표시한다 (totalCount === undefined → loading).
 */
const RECENT_ACTIVITY_LIMIT = 8

function RecentActivityPanel(props: {
	readonly entries: ReadonlyArray<AuditEntry>
	readonly totalCount: number | undefined
	readonly onGoAudit: () => void
}) {
	const { entries, totalCount, onGoAudit } = props
	const loading = totalCount === undefined
	const recent = useMemo(() => entries.slice(0, RECENT_ACTIVITY_LIMIT), [entries])
	const hasMore = entries.length > RECENT_ACTIVITY_LIMIT
	return (
		<div className="panel service-overview-activity">
			<div className="panel-head">
				<h3>최근 활동</h3>
				<button type="button" className="btn btn-ghost btn-sm" onClick={onGoAudit}>
					전체 보기
					{typeof totalCount === "number" && (
						<span className="service-overview-activity-count">{totalCount}</span>
					)}
				</button>
			</div>
			{loading ? (
				<div className="stack" aria-hidden>
					{Array.from({ length: 3 }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
						<div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0" }}>
							<Skeleton width={8} height={8} radius={999} />
							<div style={{ flex: 1 }}>
								<Skeleton width="60%" height={12} />
								<div style={{ height: 4 }} />
								<Skeleton width="30%" height={10} />
							</div>
						</div>
					))}
				</div>
			) : recent.length === 0 ? (
				<p className="muted" style={{ fontSize: 13 }}>
					아직 기록된 활동이 없습니다.
				</p>
			) : (
				<>
					<AuditTimeline entries={recent} />
					{hasMore && (
						<button
							type="button"
							className="btn btn-ghost btn-sm service-overview-activity-more"
							onClick={onGoAudit}
						>
							+ {entries.length - RECENT_ACTIVITY_LIMIT}개 더 보기 →
						</button>
					)}
				</>
			)}
		</div>
	)
}

function OntologyTab(props: {
	readonly client: AdminClient
	readonly service: ServiceSummary
	readonly refresh: () => Promise<void>
}) {
	return (
		<OntologyEditor
			slug={props.service.serviceSlug}
			initial={props.service.ontologySlice}
			client={props.client}
			onSaved={props.refresh}
		/>
	)
}

function ServersTab(props: {
	readonly auth: AuthContext
	readonly client: AdminClient
	readonly service: ServiceSummary
	readonly onCount?: (n: number) => void
}) {
	const { client, service, onCount } = props
	const slug = service.serviceSlug
	const [createOpen, setCreateOpen] = useState(false)

	// 서버 목록 + 툴 카탈로그를 한 번에 로드. refresh() 로 생성 직후 재호출(카탈로그 포함 재조회).
	const { data, error, refresh } = useAsyncData<{
		readonly servers: ReadonlyArray<McpServerSummary>
		readonly catalog: ToolCatalog
	}>(async () => {
		const [servers, catalog] = await Promise.all([client.listMcpServers(slug), client.getToolCatalog()])
		return { servers, catalog }
	}, [client, slug])
	const servers = data?.servers ?? []
	const catalog = data?.catalog

	// 목록 로드 성공 시점에 카운트 보고 — 기존 onCount 호출 시점(then 성공) 보존.
	useEffect(() => {
		if (data) onCount?.(data.servers.length)
	}, [data, onCount])

	// 스켈레톤은 최초 로드(아직 데이터 없음)에만 — refresh() 재호출 시엔 기존 목록 유지(기존 동등성).
	const showSkeleton = !error && data === undefined

	return (
		<>
			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 18 }}>
					{error}
				</div>
			)}

			<div className="panel">
				<div className="panel-head">
					<h3>MCP 서버</h3>
					<button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
						새 MCP 서버
					</button>
				</div>

				{showSkeleton ? (
					<div className="stack" aria-hidden>
						{Array.from({ length: 2 }, (_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
							<div key={i} className="skel-card">
								<div className="skel-card__head">
									<Skeleton width={28} height={28} radius={8} />
									<div className="skel-card__title-group">
										<Skeleton width="45%" height={14} />
										<Skeleton width="65%" height={11} />
									</div>
									<Skeleton width={80} height={18} radius={999} />
								</div>
								<SkeletonText lines={2} lineHeight={11} />
							</div>
						))}
					</div>
				) : servers.length === 0 ? (
					<p className="muted" style={{ fontSize: 13 }}>
						아직 생성된 MCP 서버가 없습니다. "새 MCP 서버" 로 추가하세요.
					</p>
				) : (
					<div className="stack">
						{servers.map((server) => (
							<ServerCard key={server.id} baseUrl={props.auth.baseUrl} slug={slug} server={server} />
						))}
					</div>
				)}
			</div>

			{createOpen && (
				<ServerCreateModal
					slug={slug}
					catalog={catalog}
					onClose={() => setCreateOpen(false)}
					onCreate={async (input) => {
						await client.createMcpServer(slug, input)
						refresh()
					}}
				/>
			)}
		</>
	)
}

function ServerCard(props: {
	readonly baseUrl: string
	readonly slug: string
	readonly server: McpServerSummary
}) {
	const endpoint = `${props.baseUrl}/mcp/${props.slug}/${props.server.name}`

	return (
		<div
			style={{
				border: "1px solid var(--border)",
				borderRadius: "var(--radius)",
				padding: "14px 16px",
				background: "var(--bg-elev)"
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					gap: 16
				}}
			>
				<div style={{ minWidth: 0, flex: 1 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
						<strong style={{ fontSize: 14 }}>{props.server.displayName}</strong>
						<code className="inline">{props.server.name}</code>
						<span className="badge">{props.server.enabledTools.length}개 도구</span>
					</div>
					{props.server.description && (
						<div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
							{props.server.description}
						</div>
					)}
					{props.server.enabledTools.length > 0 && (
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 4,
								marginBottom: 10
							}}
							aria-label={`활성화된 도구 ${props.server.enabledTools.length}개`}
						>
							{props.server.enabledTools.slice(0, 8).map((tool) => (
								<span key={tool} className="chip">
									{tool}
								</span>
							))}
							{props.server.enabledTools.length > 8 && (
								<span
									className="muted"
									style={{ fontSize: 11, alignSelf: "center", marginLeft: 2 }}
									title={props.server.enabledTools.slice(8).join(", ")}
								>
									+{props.server.enabledTools.length - 8}
								</span>
							)}
						</div>
					)}
					<CopyRow value={endpoint} />
				</div>
				<Link
					to={`/services/${props.slug}/servers/${props.server.name}`}
					className="btn btn-ghost btn-sm"
					style={{ flexShrink: 0, textDecoration: "none" }}
				>
					상세
				</Link>
			</div>
		</div>
	)
}

function AuditTab(props: {
	readonly client: AdminClient
	readonly slug: string
	readonly onCount?: (n: number) => void
}) {
	const { client, slug, onCount } = props
	const { data: entries, error } = useAsyncData<ReadonlyArray<AuditEntry>>(
		() => client.listAudit(slug, 200),
		[client, slug]
	)

	// 목록 로드 성공 시점에 카운트 보고 — 기존 onCount 호출 시점(then 성공) 보존.
	useEffect(() => {
		if (entries) onCount?.(entries.length)
	}, [entries, onCount])

	// 스켈레톤은 최초 로드(아직 데이터 없음)에만 — 기존 loading=true 초기값 동등성.
	const showSkeleton = !error && entries === undefined

	return (
		<div className="panel">
			<h3>감사 로그</h3>
			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 14 }}>
					{error}
				</div>
			)}
			{showSkeleton ? (
				<div className="stack" aria-hidden>
					{Array.from({ length: 5 }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
						<div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0" }}>
							<Skeleton width={8} height={8} radius={999} />
							<div style={{ flex: 1 }}>
								<Skeleton width="55%" height={12} />
								<div style={{ height: 4 }} />
								<Skeleton width="25%" height={10} />
							</div>
						</div>
					))}
				</div>
			) : (
				<AuditTimeline entries={entries ?? []} />
			)}
		</div>
	)
}
